import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AssessmentHistory from "../components/AssessmentHistory";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Target, CheckCircle, Sparkles, Lightbulb, ArrowRight, Loader2, 
  Heart, BookOpen, AlertCircle, PenLine, ChevronDown, ChevronUp, History, ClipboardList 
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Assessment = () => {
  const { user, token, updateUser } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab state - default to 'take' unless 'history' is in URL
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'take');
  
  // States
  const [step, setStep] = useState("check"); // check, pre-assessment, assessment, results
  const [preAssessmentCompleted, setPreAssessmentCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Pre-assessment states
  const [preAssessmentData, setPreAssessmentData] = useState({
    interests: "",
    ambitions: "",
    preferred_field: "",
    favorite_subjects: "",
    additional_info: ""
  });
  
  // Assessment states
  const [aiQuestions, setAiQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [customAnswers, setCustomAnswers] = useState({}); // Store custom text answers
  const [showCustomInput, setShowCustomInput] = useState({}); // Track which questions have custom input expanded
  const [submitting, setSubmitting] = useState(false);
  
  // Results state
  const [results, setResults] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(15);

  // Inspiring messages for countdown
  const inspiringMessages = [
    "🌟 Analyzing your unique strengths and interests...",
    "🎯 Matching your profile with thousands of career paths...",
    "💡 Discovering opportunities that align with your ambitions...",
    "🚀 Your personalized career roadmap is being crafted...",
    "✨ AI is evaluating your responses for perfect matches...",
    "🎓 Finding the best learning paths for your goals...",
    "🌈 Your future career is taking shape...",
    "💪 Identifying skills that will make you stand out...",
    "🔍 Deep analysis in progress for accurate recommendations...",
    "🎉 Almost there! Preparing your career blueprint..."
  ];

  useEffect(() => {
    checkPreAssessmentStatus();
  }, []);

  const checkPreAssessmentStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pre-assessment/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.completed) {
        setPreAssessmentCompleted(true);
        setStep("pre-assessment-done");
        toast.info("You've already completed the pre-assessment. Click 'Start Assessment' to begin.");
      } else {
        setStep("pre-assessment");
      }
    } catch (error) {
      toast.error("Failed to check assessment status");
    } finally {
      setLoading(false);
    }
  };

  const handlePreAssessmentSubmit = async () => {
    // Validation
    if (!preAssessmentData.interests || !preAssessmentData.ambitions || 
        !preAssessmentData.preferred_field || !preAssessmentData.favorite_subjects) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      
      const data = {
        interests: preAssessmentData.interests.split(',').map(s => s.trim()).filter(s => s),
        ambitions: preAssessmentData.ambitions,
        preferred_field: preAssessmentData.preferred_field,
        favorite_subjects: preAssessmentData.favorite_subjects.split(',').map(s => s.trim()).filter(s => s),
        additional_info: preAssessmentData.additional_info
      };

      const response = await axios.post(`${API}/pre-assessment`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.questions) {
        setAiQuestions(response.data.questions);
        setPreAssessmentCompleted(true);
        toast.success("Pre-assessment completed! Your personalized questions are ready.");
        setStep("assessment");
      }
    } catch (error) {
      toast.error("Failed to submit pre-assessment");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const startAssessment = async () => {
    try {
      setLoading(true);
      
      // Fetch AI-generated questions if not already loaded
      if (aiQuestions.length === 0) {
        // Trigger AI generation by re-submitting pre-assessment data
        const response = await axios.get(`${API}/pre-assessment/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.data) {
          const preAssessResponse = await axios.post(`${API}/pre-assessment`, response.data.data, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (preAssessResponse.data.questions) {
            setAiQuestions(preAssessResponse.data.questions);
          }
        }
      }
      
      setStep("assessment");
    } catch (error) {
      toast.error("Failed to start assessment");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleMultiAnswer = (questionId, option, checked) => {
    const currentAnswers = answers[questionId] || [];
    let newAnswers;
    
    if (checked) {
      newAnswers = [...currentAnswers, option];
    } else {
      newAnswers = currentAnswers.filter(a => a !== option);
    }
    
    setAnswers({ ...answers, [questionId]: newAnswers });
  };

  // Handle custom text answer
  const handleCustomAnswer = (questionId, text) => {
    setCustomAnswers({ ...customAnswers, [questionId]: text });
  };

  // Toggle custom input visibility
  const toggleCustomInput = (questionId) => {
    setShowCustomInput(prev => ({ 
      ...prev, 
      [questionId]: !prev[questionId] 
    }));
  };

  const handleNext = () => {
    const currentQ = aiQuestions[currentQuestion];
    const answer = answers[currentQ.id];
    const customAnswer = customAnswers[currentQ.id];
    
    // Allow proceeding if either a selected answer OR custom answer is provided
    const hasSelectedAnswer = answer && (Array.isArray(answer) ? answer.length > 0 : true);
    const hasCustomAnswer = customAnswer && customAnswer.trim().length > 0;
    
    if (!hasSelectedAnswer && !hasCustomAnswer) {
      toast.error("Please select an answer or write your own response");
      return;
    }
    
    // Check if multi-choice has minimum selections (only if no custom answer)
    if (currentQ.type === "multi" && Array.isArray(answer) && !hasCustomAnswer) {
      const maxSelections = currentQ.max_selections || 2;
      if (answer.length < maxSelections) {
        toast.error(`Please select at least ${maxSelections} options or write your own answer`);
        return;
      }
    }
    
    if (currentQuestion < aiQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    const currentQ = aiQuestions[currentQuestion];
    const answer = answers[currentQ.id];
    const customAnswer = customAnswers[currentQ.id];
    
    const hasSelectedAnswer = answer && (Array.isArray(answer) ? answer.length > 0 : true);
    const hasCustomAnswer = customAnswer && customAnswer.trim().length > 0;
    
    if (!hasSelectedAnswer && !hasCustomAnswer) {
      toast.error("Please answer the current question or write your own response");
      return;
    }

    // Check if all questions have been answered (selected OR custom)
    const unansweredQuestions = aiQuestions.filter(q => {
      const selectedAnswer = answers[q.id];
      const customText = customAnswers[q.id];
      const hasSelected = selectedAnswer && (Array.isArray(selectedAnswer) ? selectedAnswer.length > 0 : true);
      const hasCustom = customText && customText.trim().length > 0;
      return !hasSelected && !hasCustom;
    });

    if (unansweredQuestions.length > 0) {
      toast.error(`Please answer all questions. ${unansweredQuestions.length} question(s) remaining.`);
      return;
    }

    // Show countdown modal
    setShowCountdown(true);
    setCountdown(15);

    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
        }
        return prev - 1;
      });
    }, 1000);

    try {
      setSubmitting(true);
      
      // Combine selected answers with custom answers for submission
      const combinedAnswers = {};
      aiQuestions.forEach(q => {
        combinedAnswers[q.id] = {
          selected_answer: answers[q.id] || null,
          custom_answer: customAnswers[q.id] || null
        };
      });
      
      // Add a minimum delay for countdown experience
      const [response] = await Promise.all([
        axios.post(
          `${API}/assessments/submit`,
          {
            assessment_id: "ai_generated_assessment",
            answers: combinedAnswers,
            questions: aiQuestions, // Include questions for history
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        new Promise(resolve => setTimeout(resolve, 15000)) // 15 seconds countdown
      ]);

      clearInterval(timer);
      setShowCountdown(false);
      setResults(response.data);
      setStep("results");
      toast.success("Assessment completed! Check your personalized career recommendations.");
      
      // Update user in context
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateUser(userResponse.data);
    } catch (error) {
      clearInterval(timer);
      setShowCountdown(false);
      toast.error("Failed to submit assessment");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle tab change
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  // Main Assessment UI with Tabs
  const renderAssessmentContent = () => {
    // Pre-Assessment Form UI
    if (step === "pre-assessment") {
      return (
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                <Lightbulb className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">Before We Begin...</h1>
                <p className="text-gray-600 text-sm md:text-base">
                  Help us understand your interests and goals
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
              <AlertCircle className="w-3 h-3 mr-1" />
              Mandatory • Takes 2-3 minutes
            </Badge>
          </div>

          <Card className="border-none shadow-lg">
            <div className="h-2 bg-gradient-to-r from-primary via-primary/80 to-primary/60"></div>
            <CardHeader>
              <CardTitle>Tell Us About Yourself</CardTitle>
              <CardDescription>
                Based on your responses, our AI will generate 10 personalized assessment questions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="interests" className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  What are your interests? *
                </Label>
                <Input
                  id="interests"
                  value={preAssessmentData.interests}
                  onChange={(e) => setPreAssessmentData({...preAssessmentData, interests: e.target.value})}
                  placeholder="e.g., Technology, Sports, Arts, Music, Science (comma-separated)"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="ambitions" className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-500" />
                  What are your career ambitions? *
                </Label>
                <Textarea
                  id="ambitions"
                  value={preAssessmentData.ambitions}
                  onChange={(e) => setPreAssessmentData({...preAssessmentData, ambitions: e.target.value})}
                  placeholder="Describe your career goals and what you want to achieve..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="preferred_field" className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-yellow-500" />
                  Which career field interests you most? *
                </Label>
                <Input
                  id="preferred_field"
                  value={preAssessmentData.preferred_field}
                  onChange={(e) => setPreAssessmentData({...preAssessmentData, preferred_field: e.target.value})}
                  placeholder="e.g., Software Engineering, Medicine, Business, Design"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="subjects" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-blue-500" />
                  What are your favorite subjects? *
                </Label>
                <Input
                  id="subjects"
                  value={preAssessmentData.favorite_subjects}
                  onChange={(e) => setPreAssessmentData({...preAssessmentData, favorite_subjects: e.target.value})}
                  placeholder="e.g., Mathematics, Physics, Literature (comma-separated)"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="additional_info">Additional Information (Optional)</Label>
                <Textarea
                  id="additional_info"
                  value={preAssessmentData.additional_info}
                  onChange={(e) => setPreAssessmentData({...preAssessmentData, additional_info: e.target.value})}
                  placeholder="Any other information that might help..."
                  rows={2}
                  className="mt-1"
                />
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-indigo-900 mb-1">AI-Powered Assessment</p>
                    <p className="text-sm text-indigo-700">
                      Our AI will analyze your profile and generate 10 personalized questions. You'll then receive career recommendations tailored specifically to you.
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handlePreAssessmentSubmit} 
                disabled={loading}
                className="w-full shadow-md hover:shadow-lg transition-shadow"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Your Questions...
                  </>
                ) : (
                  <>
                    Generate My Assessment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Pre-assessment completed, ready to start
    if (step === "pre-assessment-done") {
      return (
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="inline-block p-4 bg-gradient-to-br from-success to-success/80 rounded-xl shadow-lg mb-6">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">You're All Set!</h1>
          <p className="text-lg md:text-xl text-gray-600 mb-8">
            Your personalized assessment is ready. We've prepared 10 questions based on your interests and goals.
          </p>
          <Button size="lg" onClick={startAssessment} disabled={loading} className="shadow-lg hover:shadow-xl transition-shadow">
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Loading Assessment...
              </>
            ) : (
              <>
                <Target className="w-5 h-5 mr-2" />
                Start My Assessment
              </>
            )}
          </Button>
        </div>
      );
    }

    // Loading state
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  };

  // Pre-Assessment Form UI
  if (step === "pre-assessment") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-6 md:py-8">
          {/* Tabs for Take Assessment / History */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="max-w-3xl mx-auto mb-6">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="take" className="flex items-center gap-2" data-testid="take-assessment-tab">
                  <ClipboardList className="w-4 h-4" />
                  Take Assessment
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center gap-2" data-testid="assessment-history-tab">
                  <History className="w-4 h-4" />
                  Assessment History
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="take">
              {renderAssessmentContent()}
            </TabsContent>
            
            <TabsContent value="history">
              <div className="max-w-4xl mx-auto">
                <AssessmentHistory />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    );
  }

  // Pre-assessment completed, ready to start
  if (step === "pre-assessment-done") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-6 md:py-8">
          {/* Tabs for Take Assessment / History */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="max-w-3xl mx-auto mb-6">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="take" className="flex items-center gap-2" data-testid="take-assessment-tab">
                  <ClipboardList className="w-4 h-4" />
                  Take Assessment
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center gap-2" data-testid="assessment-history-tab">
                  <History className="w-4 h-4" />
                  Assessment History
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="take">
              <div className="max-w-2xl mx-auto text-center py-12">
                <div className="inline-block p-4 bg-gradient-to-br from-success to-success/80 rounded-xl shadow-lg mb-6">
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">You're All Set!</h1>
                <p className="text-lg md:text-xl text-gray-600 mb-8">
                  Your personalized assessment is ready. We've prepared 10 questions based on your interests and goals.
                </p>
                <Button size="lg" onClick={startAssessment} disabled={loading} className="shadow-lg hover:shadow-xl transition-shadow">
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Loading Assessment...
                    </>
                  ) : (
                    <>
                      <Target className="w-5 h-5 mr-2" />
                      Start My Assessment
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="history">
              <div className="max-w-4xl mx-auto">
                <AssessmentHistory />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    );
  }

  // Assessment Questions
  if (step === "assessment" && aiQuestions.length > 0) {
    const currentQ = aiQuestions[currentQuestion];
    const progress = ((currentQuestion + 1) / aiQuestions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Career Assessment</h2>
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                  Question {currentQuestion + 1} of {aiQuestions.length}
                </Badge>
              </div>
              <Progress value={progress} className="h-3 shadow-sm" />
            </div>

            <Card className="border-none shadow-lg">
              <div className="h-2 bg-gradient-to-r from-accent via-accent/80 to-accent/60"></div>
              <CardHeader>
                <CardTitle className="text-xl md:text-2xl">{currentQ.question}</CardTitle>
                {currentQ.category && (
                  <CardDescription>
                    Category: {currentQ.category}
                    {currentQ.type === "multi" && (
                      <span className="ml-2 text-primary font-semibold">
                        (Select {currentQ.max_selections || 2} options)
                      </span>
                    )}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {currentQ.type === "multi" ? (
                  // Multi-choice questions with checkboxes
                  <div className="space-y-3">
                    {currentQ.options.map((option, idx) => {
                      const isChecked = (answers[currentQ.id] || []).includes(option);
                      const maxSelections = currentQ.max_selections || 2;
                      const currentSelections = (answers[currentQ.id] || []).length;
                      const isDisabled = !isChecked && currentSelections >= maxSelections;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all ${
                            isChecked 
                              ? 'bg-indigo-50 border-indigo-500' 
                              : isDisabled
                              ? 'bg-gray-100 border-gray-300 opacity-50'
                              : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-indigo-300'
                          }`}
                        >
                          <Checkbox
                            id={`${currentQ.id}-${idx}`}
                            checked={isChecked}
                            disabled={isDisabled}
                            onCheckedChange={(checked) => handleMultiAnswer(currentQ.id, option, checked)}
                          />
                          <Label 
                            htmlFor={`${currentQ.id}-${idx}`} 
                            className={`flex-1 cursor-pointer ${isDisabled ? 'cursor-not-allowed' : ''}`}
                          >
                            {option}
                          </Label>
                        </div>
                      );
                    })}
                    <p className="text-sm text-gray-500 mt-2">
                      Selected: {(answers[currentQ.id] || []).length} of {currentQ.max_selections || 2}
                    </p>
                  </div>
                ) : (
                  // Single-choice questions with radio buttons
                  <RadioGroup
                    value={answers[currentQ.id]}
                    onValueChange={(value) => handleAnswer(currentQ.id, value)}
                  >
                    {currentQ.options.map((option, idx) => (
                      <div key={idx} className="flex items-center space-x-2 p-3 hover:bg-gray-50 rounded-lg border border-transparent hover:border-indigo-200">
                        <RadioGroupItem value={option} id={`${currentQ.id}-${idx}`} />
                        <Label htmlFor={`${currentQ.id}-${idx}`} className="flex-1 cursor-pointer">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}

                {/* Custom Answer Section */}
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={() => toggleCustomInput(currentQ.id)}
                    className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                    data-testid={`toggle-custom-answer-${currentQ.id}`}
                  >
                    <PenLine className="w-4 h-4" />
                    Write your own answer (optional)
                    {showCustomInput[currentQ.id] ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {showCustomInput[currentQ.id] && (
                    <div className="mt-3 animate-in slide-in-from-top-2 duration-200">
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-200">
                        <div className="flex items-start gap-2 mb-2">
                          <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-gray-600">
                            If the options above don't match your thoughts, write your own answer. 
                            Our AI will analyze your response to provide more personalized career recommendations.
                          </p>
                        </div>
                        <Textarea
                          value={customAnswers[currentQ.id] || ""}
                          onChange={(e) => handleCustomAnswer(currentQ.id, e.target.value)}
                          placeholder="Express your thoughts in your own words..."
                          className="bg-white border-gray-300 focus:border-indigo-500 resize-none"
                          rows={3}
                          maxLength={500}
                          data-testid={`custom-answer-input-${currentQ.id}`}
                        />
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-gray-500">
                            {(customAnswers[currentQ.id] || "").length}/500 characters
                          </span>
                          {customAnswers[currentQ.id] && customAnswers[currentQ.id].trim().length > 0 && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Custom answer added
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  {currentQuestion > 0 && (
                    <Button variant="outline" onClick={handlePrevious} size="lg">
                      Previous
                    </Button>
                  )}
                  {currentQuestion < aiQuestions.length - 1 ? (
                    <Button onClick={handleNext} className="ml-auto shadow-md hover:shadow-lg transition-shadow" size="lg">
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button onClick={handleSubmit} disabled={submitting} className="ml-auto shadow-md hover:shadow-lg transition-shadow" size="lg">
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Assessment
                          <CheckCircle className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Countdown Modal */}
            <Dialog open={showCountdown} onOpenChange={() => {}}>
              <DialogContent className="sm:max-w-md" hideClose>
                <div className="text-center py-6">
                  {/* Animated Circle Countdown */}
                  <div className="relative inline-flex items-center justify-center mb-6">
                    <svg className="transform -rotate-90 w-32 h-32">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        className="text-gray-200"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 56}`}
                        strokeDashoffset={`${2 * Math.PI * 56 * (1 - countdown / 15)}`}
                        className="text-indigo-600 transition-all duration-1000 ease-linear"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-indigo-600">{countdown}</div>
                        <div className="text-xs text-gray-500 mt-1">seconds</div>
                      </div>
                    </div>
                  </div>

                  {/* Rotating Messages */}
                  <div className="min-h-[60px] flex items-center justify-center px-4">
                    <p className="text-lg font-medium text-gray-700 animate-pulse">
                      {inspiringMessages[Math.floor((15 - countdown) / 1.5) % inspiringMessages.length]}
                    </p>
                  </div>

                  {/* Animated Sparkles */}
                  <div className="flex justify-center gap-2 mt-4">
                    <Sparkles className="w-5 h-5 text-yellow-500 animate-bounce" style={{animationDelay: '0ms'}} />
                    <Sparkles className="w-5 h-5 text-indigo-500 animate-bounce" style={{animationDelay: '150ms'}} />
                    <Sparkles className="w-5 h-5 text-purple-500 animate-bounce" style={{animationDelay: '300ms'}} />
                  </div>

                  <p className="text-sm text-gray-500 mt-4">
                    Please wait while we analyze your responses...
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (step === "results" && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-success to-success/80 flex items-center justify-center shadow-md">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">Assessment Complete!</h1>
                  <p className="text-gray-600 text-sm md:text-base">
                    Your personalized career recommendations are ready
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="text-base py-2 px-4 bg-primary/10 text-primary border-primary/20">
                  +50 Points Earned
                </Badge>
                <Badge variant="outline" className="text-base py-2 px-4 bg-success/10 text-success border-success/20">
                  Assessment Completed Badge
                </Badge>
              </div>
            </div>

            <Card className="mb-6 border-none shadow-lg">
              <div className="h-2 bg-gradient-to-r from-warning via-warning/80 to-warning/60"></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Sparkles className="w-6 h-6 text-warning" />
                  Your Personalized Career Roadmap
                </CardTitle>
                <CardDescription>
                  Detailed career paths based on your profile, interests, and assessment
                </CardDescription>
              </CardHeader>
              <CardContent>
                {results.detailed_recommendations && results.detailed_recommendations.length > 0 ? (
                  <div className="space-y-8">
                    {results.detailed_recommendations.map((career, idx) => (
                      <div key={idx} className="border-l-4 border-indigo-500 pl-6 pr-4 py-4 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 rounded-r-lg">
                        {/* Career Header */}
                        <div className="mb-4">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-2xl font-bold text-indigo-900">{idx + 1}. {career.career_name}</h3>
                            <Badge variant="outline" className="bg-white">{career.rarity || "Recommended"}</Badge>
                          </div>
                          {career.salary_range && (
                            <p className="text-sm text-green-700 font-semibold">💰 {career.salary_range}</p>
                          )}
                          {career.job_outlook && (
                            <p className="text-sm text-blue-700 mt-1">📈 {career.job_outlook}</p>
                          )}
                        </div>

                        {/* Why Good Fit */}
                        <div className="mb-4 p-4 bg-white/70 rounded-lg">
                          <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                            <Heart className="w-4 h-4" />
                            Why This Career Fits You
                          </h4>
                          <p className="text-gray-700 leading-relaxed">{career.why_good_fit}</p>
                        </div>

                        {/* What to Study */}
                        {career.what_to_study && career.what_to_study.length > 0 && (
                          <div className="mb-4 p-4 bg-white/70 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                              <BookOpen className="w-4 h-4" />
                              What to Study
                            </h4>
                            <ul className="space-y-1">
                              {career.what_to_study.map((subject, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-indigo-600 mt-1">▹</span>
                                  <span className="text-gray-700">{subject}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Skills to Develop */}
                        {career.skills_to_develop && career.skills_to_develop.length > 0 && (
                          <div className="mb-4 p-4 bg-white/70 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                              <Target className="w-4 h-4" />
                              Skills to Develop
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {career.skills_to_develop.map((skill, i) => (
                                <Badge key={i} variant="secondary" className="bg-indigo-100 text-indigo-800">
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Certifications & Tools */}
                        {career.certifications && career.certifications.length > 0 && (
                          <div className="mb-4 p-4 bg-white/70 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Recommended Certifications & Tools
                            </h4>
                            <ul className="space-y-1">
                              {career.certifications.map((cert, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-green-600 mt-1">✓</span>
                                  <span className="text-gray-700">{cert}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Learning Resources */}
                        {career.learning_resources && career.learning_resources.length > 0 && (
                          <div className="mb-4 p-4 bg-white/70 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                              <Lightbulb className="w-4 h-4" />
                              Learning Resources
                            </h4>
                            <ul className="space-y-1">
                              {career.learning_resources.map((resource, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-purple-600 mt-1">🔗</span>
                                  <span className="text-gray-700">{resource}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Step-by-Step Roadmap */}
                        {career.roadmap && career.roadmap.length > 0 && (
                          <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border-2 border-indigo-200">
                            <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2 text-lg">
                              <ArrowRight className="w-5 h-5" />
                              Your Step-by-Step Roadmap
                            </h4>
                            <div className="space-y-3">
                              {career.roadmap.map((step, i) => (
                                <div key={i} className="flex gap-3">
                                  <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                                    {i + 1}
                                  </div>
                                  <div className="flex-1">
                                    <h5 className="font-semibold text-indigo-800 mb-1">{step.phase}</h5>
                                    <p className="text-gray-700 text-sm leading-relaxed">{step.action}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  // Fallback to legacy recommendations if detailed ones not available
                  <div className="space-y-4">
                    {results.recommendations.map((rec, idx) => (
                      <div key={idx} className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                        <p className="text-gray-800 leading-relaxed">{rec}</p>
                      </div>
                    ))}
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <p className="font-semibold text-yellow-800">Want More Detailed Guidance?</p>
                          <p className="text-sm text-yellow-700 mt-1">Retake the assessment to receive a comprehensive career roadmap with step-by-step guidance, learning resources, and skills development plan.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate("/careers")} size="lg">
                Explore Careers
              </Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")} size="lg">
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
      <Footer />
    </div>
  );
};

export default Assessment;
